import {
  loadSettings,
  notifySettingsChange,
  SETTINGS_LIMITS,
  type GameSettings as GameSettingsConfig,
} from "../store/settingsStore";

/**
 * GameSettings — a gear button pinned to the top-right corner that opens a
 * small panel for mouse sensitivity + master volume. Both persist to
 * localStorage (settingsStore) and apply live: the FirstPersonController
 * re-reads the sensitivity multiplier and main.ts scales the SoundManager
 * volume, all via the store's subscribe channel.
 *
 * Opening the panel releases pointer-lock (so the cursor is usable) and
 * sets `body.settings-open`, which the controller's canvas-click handler
 * checks to avoid silently re-grabbing the lock — mirrors the BuyMenu.
 * In-match: press Esc once (the browser frees the cursor), then click the
 * gear. Esc also closes the panel while it's open.
 */
export class GameSettings {
  private readonly gear: HTMLButtonElement;
  private readonly root: HTMLDivElement;
  private cfg: GameSettingsConfig;
  private isOpen = false;
  private readonly onKeyDown: (e: KeyboardEvent) => void;

  constructor() {
    this.cfg = loadSettings();
    this.injectStyles();

    this.gear = document.createElement("button");
    this.gear.id = "fps-settings-gear";
    this.gear.type = "button";
    this.gear.title = "Settings — sensitivity & sound (Esc frees the cursor)";
    this.gear.setAttribute("aria-label", "Settings");
    this.gear.innerHTML = gearSvg();
    this.gear.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });
    document.body.appendChild(this.gear);

    this.root = document.createElement("div");
    this.root.id = "fps-settings";
    this.root.classList.add("hidden");
    this.root.innerHTML = this.renderHtml();
    document.body.appendChild(this.root);
    this.bindControls();

    this.onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      // Esc closes the panel when it's open (the pointer is already free here).
      if (this.isOpen && e.code === "Escape") {
        this.close();
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", this.onKeyDown, { capture: true });
  }

  destroy(): void {
    window.removeEventListener("keydown", this.onKeyDown, { capture: true } as any);
    this.gear.remove();
    this.root.remove();
  }

  private toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  private open(): void {
    this.isOpen = true;
    this.root.classList.remove("hidden");
    this.gear.classList.add("active");
    document.body.classList.add("settings-open");
    // Free the cursor so the sliders are usable, and stop the canvas
    // click-to-lock from re-grabbing it (controller checks settings-open).
    try { document.exitPointerLock(); } catch { /* ignore */ }
  }

  private close(): void {
    this.isOpen = false;
    this.root.classList.add("hidden");
    this.gear.classList.remove("active");
    document.body.classList.remove("settings-open");
  }

  private renderHtml(): string {
    const c = this.cfg;
    const sens = SETTINGS_LIMITS.sensitivity;
    const vol = SETTINGS_LIMITS.masterVolume;
    return `
      <div class="gs-panel">
        <div class="gs-header">
          <span class="gs-title">Settings</span>
          <button class="gs-close" type="button" aria-label="Close">✕</button>
        </div>
        <div class="gs-row">
          <label>Mouse sensitivity <span class="gs-val" data-val="sensitivity">${fmtSens(c.sensitivity)}</span></label>
          <input type="range" min="${sens.min}" max="${sens.max}" step="${sens.step}" data-bind="sensitivity" value="${c.sensitivity}">
        </div>
        <div class="gs-row">
          <label>Sound volume <span class="gs-val" data-val="masterVolume">${fmtVol(c.masterVolume)}</span></label>
          <input type="range" min="${vol.min}" max="${vol.max}" step="${vol.step}" data-bind="masterVolume" value="${c.masterVolume}">
        </div>
        <div class="gs-row gs-actions">
          <button class="gs-reset" type="button">Reset to default</button>
        </div>
      </div>`;
  }

  private bindControls(): void {
    const update = <K extends keyof GameSettingsConfig>(key: K, value: GameSettingsConfig[K]) => {
      this.cfg = { ...this.cfg, [key]: value };
      notifySettingsChange(this.cfg);
      this.refreshValues();
    };

    for (const input of Array.from(this.root.querySelectorAll<HTMLInputElement>("input[data-bind]"))) {
      const key = input.dataset.bind as keyof GameSettingsConfig;
      const handler = () => update(key, Number(input.value) as never);
      input.addEventListener("input", handler);
      input.addEventListener("change", handler);
    }

    this.root.querySelector(".gs-close")?.addEventListener("click", () => this.close());

    this.root.querySelector(".gs-reset")?.addEventListener("click", () => {
      this.cfg = { sensitivity: 1.0, masterVolume: 1.0 };
      notifySettingsChange(this.cfg);
      // Re-sync the sliders + readouts to the reset values.
      for (const input of Array.from(this.root.querySelectorAll<HTMLInputElement>("input[data-bind]"))) {
        const key = input.dataset.bind as keyof GameSettingsConfig;
        input.value = String(this.cfg[key]);
      }
      this.refreshValues();
    });
  }

  /** Update the live readout chips next to each slider. */
  private refreshValues(): void {
    const setVal = (key: keyof GameSettingsConfig, txt: string) => {
      const el = this.root.querySelector(`[data-val="${key}"]`);
      if (el) el.textContent = txt;
    };
    setVal("sensitivity", fmtSens(this.cfg.sensitivity));
    setVal("masterVolume", fmtVol(this.cfg.masterVolume));
  }

  private injectStyles(): void {
    if (document.getElementById("fps-settings-styles")) return;
    const style = document.createElement("style");
    style.id = "fps-settings-styles";
    style.textContent = `
      #fps-settings-gear {
        position: fixed;
        top: 12px;
        right: 16px;
        z-index: 31;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border-radius: 8px;
        border: 1px solid rgba(255, 255, 255, 0.12);
        background: rgba(15, 16, 20, 0.78);
        color: #ffffff;
        cursor: pointer;
        backdrop-filter: blur(6px);
        transition: color 120ms ease, border-color 120ms ease, background 120ms ease;
      }
      #fps-settings-gear:hover,
      #fps-settings-gear.active {
        color: #ffffff;
        border-color: rgba(255, 255, 255, 0.5);
        background: rgba(255, 255, 255, 0.10);
      }
      #fps-settings-gear svg { width: 20px; height: 20px; display: block; }
      /* Hide the gear under full-screen overlays that own the corner. */
      body.buy-open #fps-settings-gear { display: none; }

      #fps-settings {
        position: fixed;
        top: 56px;
        right: 16px;
        z-index: 31;
        width: 280px;
        font-family: ui-sans-serif, system-ui, sans-serif;
        font-size: 13px;
        color: var(--text, #f5f5f0);
      }
      #fps-settings.hidden { display: none; }
      #fps-settings .gs-panel {
        padding: 14px 16px 12px;
        border-radius: 10px;
        border: 1px solid rgba(255, 255, 255, 0.10);
        background: rgba(15, 16, 20, 0.94);
        backdrop-filter: blur(8px);
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      #fps-settings .gs-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      #fps-settings .gs-title {
        font-size: 14px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      #fps-settings .gs-close {
        border: none;
        background: transparent;
        color: var(--text-mute, #6b6b72);
        cursor: pointer;
        font: inherit;
        font-size: 14px;
        line-height: 1;
        padding: 2px 4px;
      }
      #fps-settings .gs-close:hover { color: var(--text, #f5f5f0); }
      #fps-settings .gs-row {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      #fps-settings .gs-row label {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        font-size: 12px;
        color: var(--text-dim, #cfcfd0);
      }
      #fps-settings .gs-val {
        font-family: ui-monospace, SFMono-Regular, monospace;
        font-size: 12px;
        color: #ffffff;
      }
      #fps-settings input[type="range"] { width: 100%; accent-color: #ffffff; }
      #fps-settings .gs-actions { margin-top: 2px; }
      #fps-settings .gs-reset {
        width: 100%;
        padding: 7px 8px;
        border-radius: 6px;
        border: 1px solid rgba(255, 255, 255, 0.10);
        background: rgba(255, 255, 255, 0.04);
        color: var(--text, #f5f5f0);
        cursor: pointer;
        font: inherit;
        font-size: 11px;
        font-weight: 600;
      }
      #fps-settings .gs-reset:hover { border-color: rgba(255, 255, 255, 0.22); }
    `;
    document.head.appendChild(style);
  }
}

/** Sensitivity shown as a ×multiplier (e.g. "1.00×"). */
function fmtSens(v: number): string {
  return `${v.toFixed(2)}×`;
}

/** Master volume shown as a percentage (e.g. "80%"). */
function fmtVol(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function gearSvg(): string {
  return `
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" stroke="currentColor" stroke-width="1.6"/>
      <path d="M19.4 13c.04-.33.06-.66.06-1s-.02-.67-.06-1l1.7-1.32a.5.5 0 0 0 .12-.64l-1.6-2.77a.5.5 0 0 0-.6-.22l-2 .8a7.2 7.2 0 0 0-1.74-1l-.3-2.13A.5.5 0 0 0 14.2 2h-3.2a.5.5 0 0 0-.5.42l-.3 2.13c-.63.25-1.2.59-1.74 1l-2-.8a.5.5 0 0 0-.6.22l-1.6 2.77a.5.5 0 0 0 .12.64L4.6 11c-.04.33-.06.66-.06 1s.02.67.06 1l-1.7 1.32a.5.5 0 0 0-.12.64l1.6 2.77a.5.5 0 0 0 .6.22l2-.8c.54.41 1.11.75 1.74 1l.3 2.13a.5.5 0 0 0 .5.42h3.2a.5.5 0 0 0 .5-.42l.3-2.13c.63-.25 1.2-.59 1.74-1l2 .8a.5.5 0 0 0 .6-.22l1.6-2.77a.5.5 0 0 0-.12-.64L19.4 13Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
    </svg>`;
}
